import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { API_URL, api } from "../utils/api";
import { t } from "../utils/i18n";
import { getSocket } from "../utils/socket";
import { ACCENT, TRANSITION_FAST } from "../styles/tokens";
import { bucketNotifications } from "../utils/notificationBuckets";
import { useIsMobile } from "../hooks/useIsMobile";
import SideMenu from "./SideMenu";

// `hovered` : touche bleue rare (survol desktop uniquement, avant clic) — jamais en
// remplacement de `active` (noir, état sélectionné, inchangé). Un item peut être hovered
// sans être active (survol d'un autre onglet), jamais l'inverse en pratique puisqu'on ne
// survole pas la page où l'on est déjà, mais `active` garde toujours la priorité au cas où.
// Gris intermédiaire pour le survol, jamais ACCENT : depuis qu'ACCENT est noir plein (même
// couleur que l'état actif), l'utiliser ici aurait rendu un onglet survolé indiscernable d'un
// onglet réellement sélectionné.
const navIconColor = (active: boolean, hovered?: boolean) => (active ? "#0a0a0a" : hovered ? "#333333" : "#666");

const HomeIcon = ({ active, hovered }: { active: boolean; hovered?: boolean }) => {
  const c = navIconColor(active, hovered);
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill={active ? c : "none"} stroke={c} strokeWidth="2" style={{ transition: TRANSITION_FAST }}>
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  );
};
const ExploreIcon = ({ active, hovered }: { active: boolean; hovered?: boolean }) => {
  const c = navIconColor(active, hovered);
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" style={{ transition: TRANSITION_FAST }}>
      <circle cx="12" cy="12" r="10"/>
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" fill={active ? c : "none"}/>
    </svg>
  );
};
const MessageIcon = ({ active, hovered }: { active: boolean; hovered?: boolean }) => {
  const c = navIconColor(active, hovered);
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill={active ? c : "none"} stroke={c} strokeWidth="2" style={{ transition: TRANSITION_FAST }}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  );
};
const ScoreIcon = ({ active, hovered }: { active: boolean; hovered?: boolean }) => {
  const c = navIconColor(active, hovered);
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transition: TRANSITION_FAST }}>
      <path d="M12 3l7 3v5c0 4.8-3 8.9-7 10-4-1.1-7-5.2-7-10V6z" fill={active ? c : "none"}/>
      <path d="M9 12l2 2 4-4" stroke={active ? "white" : c}/>
    </svg>
  );
};
const ProfileIcon = ({ active, hovered }: { active: boolean; hovered?: boolean }) => {
  const c = navIconColor(active, hovered);
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" style={{ transition: TRANSITION_FAST }}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4" fill={active ? c : "none"}/>
    </svg>
  );
};
const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7"/>
    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);
const BellIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>
);
const SettingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
);
const PlusIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const LogoutIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="2">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);
const DotsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="#999">
    <circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/>
  </svg>
);

// Une icône par catégorie du panneau d'aperçu de la cloche — trait blanc sur fond noir plein
// (voir le badge 38px dans renderNotifPeek), plus de contraste et de caractère qu'un simple
// pictogramme noir sur gris clair.
const peekIconProps = { width: 17, height: 17, viewBox: "0 0 24 24", fill: "none", stroke: "white", strokeWidth: 1.9, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
const PeekActivitesIcon = () => <svg {...peekIconProps}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>;
const PeekDemandesIcon = () => <svg {...peekIconProps}><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="16" y1="11" x2="22" y2="11"/></svg>;
const PeekConnexionsIcon = () => <svg {...peekIconProps}><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"/></svg>;
const PeekPrestationsIcon = () => <svg {...peekIconProps}><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>;
const PeekPaiementsIcon = () => <svg {...peekIconProps}><path d="M18 6.5A6.5 6.5 0 0 0 8 13"/><path d="M8 13a6.5 6.5 0 0 0 10 5.5"/><line x1="3" y1="10" x2="14" y2="10"/><line x1="3" y1="15" x2="12" y2="15"/></svg>;

const NAV = [
  { path: "/", key: "home", Icon: HomeIcon },
  { path: "/explore", key: "explore", Icon: ExploreIcon },
  { path: "/messages", key: "messages", Icon: MessageIcon },
  { path: "/profile", key: "profile", Icon: ProfileIcon },
  { path: "/rating", key: "score", Icon: ScoreIcon },
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const { lang, token, setToken, setUser, user, sideMenuOpen: showSideMenu, setSideMenuOpen: setShowSideMenu } = useApp();
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  // Notifications non lues elles-mêmes (pas seulement leur total) — pour le panneau d'aperçu par
  // catégorie ouvert au clic sur la cloche (voir bucketNotifications), sans appel réseau
  // supplémentaire : /notifications est déjà chargée en entier juste en dessous.
  const [unreadNotifs, setUnreadNotifs] = useState<any[]>([]);
  const [showNotifPeek, setShowNotifPeek] = useState(false);
  const userName = user?.name || "Utilisateur";
  const userInitials = userName.split(" ").map((n: string) => n[0]).join("").slice(0,2).toUpperCase();
  const userHandle = "@" + (user?.name?.toLowerCase().replace(/\s/g,"") || "user");
  const location = useLocation();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const isMobile = useIsMobile();
  // Barre du bas réduite au défilement vers le bas, redéployée en remontant. Les conteneurs qui
  // défilent sont internes à la page (pas window) : les événements scroll ne remontent pas, on les
  // capte donc en phase de capture sur document.
  const [navCollapsed, setNavCollapsed] = useState(false);
  useEffect(() => {
    if (!isMobile) return;
    const last = new WeakMap<EventTarget, number>();
    const onScroll = (e: Event) => {
      const el = e.target as HTMLElement | Document;
      const y = el instanceof Document ? window.scrollY : (el as HTMLElement).scrollTop;
      const prev = last.get(el) ?? 0;
      last.set(el, y);
      const dy = y - prev;
      if (y <= 40) setNavCollapsed(false);
      else if (dy > 8) setNavCollapsed(true);
      else if (dy < -8) setNavCollapsed(false);
    };
    document.addEventListener("scroll", onScroll, { capture: true, passive: true });
    return () => document.removeEventListener("scroll", onScroll, { capture: true } as any);
  }, [isMobile]);
  useEffect(() => { setNavCollapsed(false); }, [location.pathname]);
  // Survol desktop de la nav (touche bleue rare, avant clic) — distinct de `active` (noir,
  // sélectionné), voir navIconColor plus haut.
  const [hoveredNavPath, setHoveredNavPath] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    api.get("/notifications").then(data => {
      const unread = (data?.notifications || []).filter((n: any) => !n.is_read);
      setUnreadCount(unread.length);
      setUnreadNotifs(unread);
    }).catch(() => {});
    const socket = getSocket(token);
    // Une nouvelle notification de type "message" fait aussi gonfler le badge Messages,
    // en plus du badge Notifications — les deux logos doivent réagir, comme demandé.
    const onNew = (notif: any) => {
      setUnreadCount(c => c + 1);
      setUnreadNotifs(prev => [notif, ...prev]);
      if (notif?.type === "message") setUnreadMessages(c => c + 1);
    };
    socket.on("new_notification", onNew);
    return () => { socket.off("new_notification", onNew); };
  }, [token]);

  // Recalé à chaque changement de page (et pas seulement en live) : couvre le cas où des
  // messages ont été lus pendant qu'on était sur /messages, pour que le badge redescende.
  useEffect(() => {
    if (!token) return;
    api.get("/messages/meta/unread-count").then(data => setUnreadMessages(data?.count || 0)).catch(() => {});
  }, [token, location.pathname]);

  const handleLogout = () => { setToken(null); setUser(null); localStorage.clear(); navigate("/login"); };

  // Une discussion ouverte (/messages/:id) a besoin de tout l'écran : les barres OLTODO
  // (en-tête + navigation du bas) sont alors retirées plutôt que simplement recouvertes —
  // plus fiable qu'un habillage en position fixed/z-index qui se comportait mal sur certains
  // téléphones (barres qui recouvraient la discussion au lieu de l'inverse).
  const hideMobileChrome = isMobile && (/^\/messages\/[^/]+$/.test(location.pathname) || location.pathname === "/search");

  // Panneau d'aperçu ouvert au clic sur la cloche, avant toute navigation — répartition par
  // catégorie (façon Instagram) plutôt qu'un simple total. Toujours rendu à l'intérieur d'un
  // conteneur `position:relative` (les deux boutons cloche l'ont déjà) : positionnement absolu
  // simple, pas de portail ni de calcul de coordonnées nécessaire.
  const renderNotifPeek = () => {
    if (!showNotifPeek) return null;
    const { activites, demandes, connexions, prestations, paiements } = bucketNotifications(unreadNotifs);
    const goToTab = (tab: string) => {
      setShowNotifPeek(false);
      setUnreadCount(0);
      navigate(`/notifications?tab=${tab}`);
    };
    // 5 lignes fixes (jamais une par type — voir l'inventaire des 60+ types réels dans
    // utils/notificationBuckets.ts) : le nombre de familles ne grandit plus avec le nombre de
    // fonctionnalités OLTODO, seul le contenu de chaque catégorie s'enrichit.
    const rows: { tab: string; label: string; count: number; Icon: () => React.ReactElement }[] = [
      { tab: "activites", label: t(lang, "notif_tab_activities") || "Activités", count: activites.length, Icon: PeekActivitesIcon },
      { tab: "demandes", label: t(lang, "notif_tab_requests") || "Demandes", count: demandes.length, Icon: PeekDemandesIcon },
      { tab: "connexions", label: t(lang, "notif_tab_connections") || "Connexions", count: connexions.length, Icon: PeekConnexionsIcon },
      { tab: "prestations", label: t(lang, "notif_tab_prestations") || "Prestations", count: prestations.length, Icon: PeekPrestationsIcon },
      { tab: "paiements", label: t(lang, "notif_tab_payments") || "Paiements", count: paiements.length, Icon: PeekPaiementsIcon },
    ];
    return (
      <>
        {/* Fond assombri (comme un vrai menu déroulant mobile) plutôt qu'un calque invisible —
            sans lui le panneau flottait à nu par-dessus le contenu de la page, d'où l'impression
            de désordre visuel derrière la carte. Refonte 2026-09-07 : rangées plus généreuses,
            icônes sur fond noir plutôt que gris plat (plus de contraste/caractère), badge rouge
            vif au lieu du bleu terne d'origine, "Tout voir" en vrai bouton noir plutôt qu'un
            simple lien texte — même niveau de finition que le Centre des règles. */}
        <div onClick={() => setShowNotifPeek(false)} style={{ position: "fixed", inset: 0, zIndex: 150, backgroundColor: "rgba(10,10,10,0.35)" }} />
        <div style={{ position: "absolute", top: "calc(100% + 12px)", right: 0, width: 300, backgroundColor: "white", borderRadius: 24, boxShadow: "0 20px 60px rgba(0,0,0,0.28)", zIndex: 151, overflow: "hidden" }}>
          <style>{`.notif-peek-row:hover { background-color: #F7F7F7; }`}</style>
          <div style={{ padding: "20px 20px 14px", fontSize: 18, fontWeight: 800, letterSpacing: -0.3 }}>{t(lang, "notif_title") || "Notifications"}</div>
          <div style={{ padding: "0 8px" }}>
            {rows.map(row => (
              <div
                key={row.tab}
                onClick={() => goToTab(row.tab)}
                className="notif-peek-row"
                style={{ display: "flex", alignItems: "center", gap: 14, padding: "11px 12px", borderRadius: 16, cursor: "pointer" }}
              >
                <div style={{ width: 38, height: 38, borderRadius: 12, backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <row.Icon />
                </div>
                <span style={{ flex: 1, fontSize: 14.5, fontWeight: row.count > 0 ? 700 : 500, color: row.count > 0 ? "#0a0a0a" : "#999" }}>{row.label}</span>
                {row.count > 0 && (
                  <span style={{ minWidth: 24, height: 24, padding: "0 7px", borderRadius: 12, backgroundColor: ACCENT, color: "white", fontSize: 12, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>{row.count > 9 ? "9+" : row.count}</span>
                )}
              </div>
            ))}
          </div>
          <div style={{ padding: 12 }}>
            <button onClick={() => goToTab("activites")} style={{ width: "100%", border: "none", backgroundColor: "#0a0a0a", color: "white", borderRadius: 16, padding: "13px 0", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
              {t(lang, "notif_peek_see_all") || "Tout voir"}
            </button>
          </div>
        </div>
      </>
    );
  };

  if (isMobile) {
    return (
      <>
      <div style={{ display: "flex", flexDirection: "column", height: "100%", backgroundColor: "#F5F5F5" }}>

        {/* HEADER MOBILE — la hauteur totale inclut l'encoche/safe-area en plus des 56px de
            contenu utile (padding-top: la safe-area, pas une réduction de l'espace des icônes) */}
        {!hideMobileChrome && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0,
          height: "calc(56px + env(safe-area-inset-top))",
          paddingTop: "env(safe-area-inset-top)",
          backgroundColor: "white", borderBottom: "1px solid #EFEFEF",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          paddingLeft: 16, paddingRight: 16, zIndex: 100,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }} onClick={() => navigate("/")}>
            <img src="/oltodo.png" alt="OLTODO" style={{ width: 28, height: 28, objectFit: "contain" }} />
            <span style={{ color: "#0a0a0a", fontWeight: 900, fontSize: 16, letterSpacing: 1.5 }}>OLTODO</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={() => navigate("/search")} style={{ width: 36, height: 36, borderRadius: "50%", backgroundColor: "#F5F5F5", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <SearchIcon />
            </button>
            <div style={{ position: "relative" }}>
              <button onClick={() => setShowNotifPeek(v => !v)} style={{ width: 36, height: 36, borderRadius: "50%", backgroundColor: "#F5F5F5", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <BellIcon />
                {unreadCount > 0 && (
                  <span style={{ position: "absolute", top: -2, right: -2, minWidth: 17, height: 17, padding: "0 4px", borderRadius: 9, backgroundColor: ACCENT, border: "2px solid white", color: "white", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
              {renderNotifPeek()}
            </div>
            <button onClick={() => navigate("/settings")} style={{ width: 36, height: 36, borderRadius: "50%", backgroundColor: "#F5F5F5", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <SettingsIcon />
            </button>
            <div onClick={() => setShowSideMenu(true)} style={{ width: 36, height: 36, borderRadius: "50%", backgroundColor: "#0a0a0a", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 13 }}>{user?.avatar_url ? <img src={user.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} /> : userInitials}</div>
          </div>
        </div>
        )}

        {/* CONTENU MOBILE — seul conteneur qui défile ; le body est verrouillé (index.css),
            donc plus de double-scroll ni de barre fixe qui clignote pendant le scroll élastique */}
        <div style={{
          flex: 1, overflowY: hideMobileChrome ? "hidden" : "auto", WebkitOverflowScrolling: "touch", overscrollBehavior: "contain",
          paddingTop: hideMobileChrome ? 0 : "calc(56px + env(safe-area-inset-top))",
          paddingBottom: hideMobileChrome ? 0 : "calc(70px + env(safe-area-inset-bottom))",
        }}>
          {children}
        </div>

        {/* BOTTOM NAV MOBILE — 5 boutons : Home, Explore, +, Messages, Profil. Pas d'onglet
            Score ici volontairement : on y accède uniquement via une notification post-événement,
            ou depuis la carte Score Social du Profil.
            Hauteur totale = 60px de contenu + la safe-area en plus (pas en moins). */}
        {!hideMobileChrome && (
        <div style={{
          position: "fixed", left: 16, right: 16, bottom: "calc(8px + env(safe-area-inset-bottom))",
          backgroundColor: "rgba(255,255,255,0.72)", border: "1px solid rgba(0,0,0,0.06)",
          backdropFilter: "blur(20px) saturate(180%)", WebkitBackdropFilter: "blur(20px) saturate(180%)",
          borderRadius: 31, boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
          display: "flex", alignItems: "center", height: 62, zIndex: 100,
          transformOrigin: "50% 100%",
          transform: navCollapsed ? "translateY(8px) scale(0.84)" : "none",
          transition: "transform 0.22s ease",
        }}>
          {[
            { path: "/", key: "home", Icon: HomeIcon },
            { path: "/explore", key: "explore", Icon: ExploreIcon },
          ].map(({ path, key, Icon }) => {
            const active = location.pathname === path;
            return (
              <button key={path} onClick={() => navigate(path)} style={{
                flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                border: "none", backgroundColor: "transparent", cursor: "pointer", gap: 3, padding: "6px 0",
              }}>
                <Icon active={active} />
                <span style={{ fontSize: 10, color: active ? "#0a0a0a" : "#999", fontWeight: active ? 700 : 400, opacity: navCollapsed ? 0 : 1, transition: "opacity 0.16s ease" }}>{t(lang, key)}</span>
              </button>
            );
          })}

          {/* Bouton + central */}
          <button onClick={() => navigate("/create")} style={{
            width: 50, height: 50, borderRadius: "50%",
            backgroundColor: "#0a0a0a", border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
            flexShrink: 0, margin: "0 6px",
          }}>
            <PlusIcon />
          </button>

          {[
            { path: "/messages", key: "messages", Icon: MessageIcon },
            { path: "/profile", key: "profile", Icon: ProfileIcon },
          ].map(({ path, key, Icon }) => {
            const active = location.pathname === path;
            return (
              <button key={path} onClick={() => navigate(path)} style={{
                flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                border: "none", backgroundColor: "transparent", cursor: "pointer", gap: 3, padding: "6px 0",
              }}>
                <div style={{ position: "relative" }}>
                  <Icon active={active} />
                  {path === "/messages" && unreadMessages > 0 && (
                    <span style={{ position: "absolute", top: -4, right: -8, minWidth: 15, height: 15, padding: "0 3px", borderRadius: 8, backgroundColor: ACCENT, border: "2px solid white", color: "white", fontSize: 9, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {unreadMessages > 9 ? "9+" : unreadMessages}
                    </span>
                  )}
                </div>
                <span style={{ fontSize: 10, color: active ? "#0a0a0a" : "#999", fontWeight: active ? 700 : 400, opacity: navCollapsed ? 0 : 1, transition: "opacity 0.16s ease" }}>{t(lang, key)}</span>
              </button>
            );
          })}
        </div>
        )}

      </div>
      <SideMenu open={showSideMenu} onClose={() => setShowSideMenu(false)} />
      </>
    );
  }

  // ─── DESKTOP ───────────────────────────────────────────────
  return (
    <>
    <div style={{ display: "flex", height: "100%", backgroundColor: "#F5F5F5" }}>

      {/* SIDEBAR DESKTOP */}
      <div style={{
        width: 240, backgroundColor: "white",
        borderRight: "1px solid #EFEFEF",
        position: "fixed", top: 0, left: 0, bottom: 0,
        display: "flex", flexDirection: "column",
        padding: "24px 14px", zIndex: 100,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 10px", marginBottom: 28, cursor: "pointer" }} onClick={() => navigate("/")}>
          <img src="/oltodo.png" alt="OLTODO" style={{ width: 36, height: 36, objectFit: "contain" }} />
          <span style={{ color: "#0a0a0a", fontWeight: 900, fontSize: 20, letterSpacing: 2 }}>OLTODO</span>
        </div>

        <nav style={{ flex: 1 }}>
          {NAV.map(({ path, key, Icon }) => {
            const active = location.pathname === path;
            return (
              <div key={path} onClick={() => navigate(path)} style={{
                display: "flex", alignItems: "center", gap: 14,
                padding: "13px 14px", borderRadius: 14, marginBottom: 2,
                cursor: "pointer",
                backgroundColor: active ? "#F0F0F0" : "transparent",
                fontWeight: active ? 700 : 400,
                fontSize: 15, color: active ? "#0a0a0a" : "#555",
                transition: "all 0.15s",
              }}
                onMouseOver={e => { if (!active) e.currentTarget.style.backgroundColor = "#F8F8F8"; setHoveredNavPath(path); }}
                onMouseOut={e => { if (!active) e.currentTarget.style.backgroundColor = "transparent"; setHoveredNavPath(null); }}>
                <div style={{ position: "relative" }}>
                  <Icon active={active} hovered={hoveredNavPath === path} />
                  {path === "/messages" && unreadMessages > 0 && (
                    <span style={{ position: "absolute", top: -5, right: -7, minWidth: 16, height: 16, padding: "0 3px", borderRadius: 8, backgroundColor: ACCENT, border: "2px solid white", color: "white", fontSize: 9, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {unreadMessages > 9 ? "9+" : unreadMessages}
                    </span>
                  )}
                </div>
                <span>{t(lang, key)}</span>
              </div>
            );
          })}
        </nav>

        <button onClick={() => navigate("/create")} style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          backgroundColor: "#0a0a0a", color: "white",
          borderRadius: 14, padding: "13px 20px",
          fontSize: 14, fontWeight: 700, marginBottom: 16,
          width: "100%", border: "none", cursor: "pointer",
          boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
        }}>
          <PlusIcon /> {t(lang, "publish")}
        </button>

        <div style={{ position: "relative" }}>
          <div onClick={() => (location.pathname === "/" ? setShowUserMenu(!showUserMenu) : setShowSideMenu(true))} style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "10px 12px", borderRadius: 14, cursor: "pointer",
            backgroundColor: showUserMenu ? "#F5F5F5" : "transparent",
          }}>
            <div style={{ width: 38, height: 38, borderRadius: "50%", overflow: "hidden", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>{user?.avatar_url ? <img src={user.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} /> : userInitials}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: "#0a0a0a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{userName}</div>
              <div style={{ fontSize: 11, color: "#999" }}>{userHandle}</div>
            </div>
            <DotsIcon />
          </div>

          {showUserMenu && (
            <div style={{ position: "absolute", bottom: 56, left: 0, right: 0, backgroundColor: "white", borderRadius: 16, border: "1px solid #E0E0E0", boxShadow: "0 12px 40px rgba(0,0,0,0.12)", overflow: "hidden" }}>
              <div onClick={() => { navigate("/profile"); setShowUserMenu(false); }} style={{ padding: "13px 16px", cursor: "pointer", fontSize: 14, color: "#333", display: "flex", alignItems: "center", gap: 10, borderBottom: "1px solid #F5F5F5" }}
                onMouseOver={e => (e.currentTarget.style.backgroundColor = "#F8F8F8")}
                onMouseOut={e => (e.currentTarget.style.backgroundColor = "white")}>
                <ProfileIcon active={false} /> {t(lang, "profile")}
              </div>
              <div onClick={() => { navigate("/settings"); setShowUserMenu(false); }} style={{ padding: "13px 16px", cursor: "pointer", fontSize: 14, color: "#333", display: "flex", alignItems: "center", gap: 10, borderBottom: "1px solid #F5F5F5" }}
                onMouseOver={e => (e.currentTarget.style.backgroundColor = "#F8F8F8")}
                onMouseOut={e => (e.currentTarget.style.backgroundColor = "white")}>
                <SettingsIcon /> {t(lang, "settings")}
              </div>
              <div onClick={handleLogout} style={{ padding: "13px 16px", cursor: "pointer", fontSize: 14, color: "#0a0a0a", display: "flex", alignItems: "center", gap: 10 }}
                onMouseOver={e => (e.currentTarget.style.backgroundColor = "#F8F8F8")}
                onMouseOut={e => (e.currentTarget.style.backgroundColor = "white")}>
                <LogoutIcon /> {t(lang, "logout")}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* HEADER DESKTOP */}
      <div style={{
        position: "fixed", top: 0, left: 240, right: 0, height: 60,
        backgroundColor: "white", borderBottom: "1px solid #EFEFEF",
        display: "flex", alignItems: "center", justifyContent: "flex-end",
        paddingRight: 24, gap: 10, zIndex: 99,
      }}>
        <button onClick={() => navigate("/search")} style={{ width: 40, height: 40, borderRadius: "50%", backgroundColor: "#F5F5F5", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <SearchIcon />
        </button>
        <div style={{ position: "relative" }}>
        <button onClick={() => setShowNotifPeek(v => !v)} style={{ width: 40, height: 40, borderRadius: "50%", backgroundColor: "#F5F5F5", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
          <BellIcon />
          {unreadCount > 0 && (
            <span style={{ position: "absolute", top: -2, right: -2, minWidth: 18, height: 18, padding: "0 4px", borderRadius: 9, backgroundColor: ACCENT, border: "2px solid white", color: "white", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
        {renderNotifPeek()}
        </div>
        <button onClick={() => navigate("/settings")} style={{ width: 40, height: 40, borderRadius: "50%", backgroundColor: "#F5F5F5", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <SettingsIcon />
        </button>
      </div>

      {/* MAIN DESKTOP */}
      <div style={{ marginLeft: 240, flex: 1, height: "100%", overflowY: "auto", paddingTop: 60, boxSizing: "border-box" }}>
        {children}
      </div>
    </div>
    <SideMenu open={showSideMenu} onClose={() => setShowSideMenu(false)} />
    </>
  );
}
